<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('userNotificationSettings') }}
    </h1>
    <div v-if="!isLoading" class="w-full flex justify-center">
      <Form
        :initial-values="userProfile"
        key="mainForm"
        class="w-full md:w-2/3 flex flex-col gap-3"
        v-slot="{ resetForm }"
        @submit="(values) => onSubmit(values, resetForm)"
      >
        <AppliedRequests
          :settings="notificationsForTheAppliedRequests"
          v-if="showSection(notificationsForTheAppliedRequests)"
        ></AppliedRequests>

        <ProactiveServices
          :settings="notificationForTheProactiveServices"
          v-if="showSection(notificationForTheProactiveServices)"
        />

        <DailyGoal
          :settings="notificationsForDailyGoal"
          v-if="showSection(notificationsForDailyGoal)"
        />

        <AccountActivities
          :key="userProfile?.disableSendAccountActivitiesEmail"
          :user-profile="userProfile"
          v-if="showAccountActivitiesNotificationSettings"
          @onChange="(value) => (userProfile.disableSendAccountActivitiesEmail = value)"
        />

        <FailedNotifications
          :key="userProfile?.enableSendFailedLoginNotification"
          :user-profile="userProfile"
          v-if="showFailedLoginNotificationsSection"
          @onChange="(value) => (userProfile.enableSendFailedLoginNotification = value)"
        />

        <div class="w-full flex justify-end gap-x-4">
          <button
            class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faDiskette class="w-4 fill-white"></faDiskette>
            </div>
            <span class="w-full text-center">
              {{ $t('Save') }}
            </span>
            <div class="p-0.5"></div>
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import client_config from '@/assets/json/client_config.json'
import client_lookups from '@/assets/json/clientLookups.json'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import AccountActivities from '@/views/auth-user/notification-settings/AccountActivities.vue'
import AppliedRequests from '@/views/auth-user/notification-settings/AppliedRequests.vue'
import DailyGoal from '@/views/auth-user/notification-settings/DailyGoal.vue'
import FailedNotifications from '@/views/auth-user/notification-settings/FailedLogin.vue'
import ProactiveServices from '@/views/auth-user/notification-settings/ProactiveServices.vue'
import { notify } from 'notiwind'
import { Form } from 'vee-validate'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const authStore = useAuthStore()
const userProfile = ref()
const isLoading = ref(true)

const {
  application: {
    resident,
    citizen,
    TypingCenter,
    establishment,
    gcc_citizen,
    familyMemberResidencyHolder,
    dubaiResident
  }
} = client_lookups

const notificationsForTheAppliedRequests = [
  {
    name: 'allowSendSMS',
    label: 'AllowSendSMS',
    show: true
  },
  {
    name: 'allowSendEmail',
    label: 'AllowSendEmail',
    show: true
  },
  {
    name: 'allowPushNotificationFlag',
    label: 'AllowPushNotification',
    show: [resident, citizen].includes(authStore.user.applicationId)
  }
]

const notificationsForDailyGoal = [
  {
    name: 'enableSendUserPerformanceNotifications',
    label: 'enableNotificationDailyGoal',
    show: [TypingCenter, establishment].includes(authStore.user.applicationId)
  }
]

const notificationForTheProactiveServices = [
  {
    name: 'allowSendProactiveEmail',
    label: 'allowToSendProactiveEmail',
    show: [resident, citizen, establishment].includes(authStore.user.applicationId)
  }
]

const showFailedLoginNotificationsSection = computed(() => {
  const { enableFailedLoginNotificationSetting } = client_config
  const isValidUserType = [
    resident,
    citizen,
    gcc_citizen,
    familyMemberResidencyHolder,
    dubaiResident,
    TypingCenter,
    establishment
  ].includes(authStore.user.applicationId)

  return isValidUserType && enableFailedLoginNotificationSetting
})

const showAccountActivitiesNotificationSettings = computed(() => {
  const { enableSendAccountActivitiesByEmail } = client_config
  const isValidUserType = [resident, citizen, gcc_citizen].includes(authStore.user.applicationId)
  return isValidUserType && enableSendAccountActivitiesByEmail
})

onMounted(async () => {
  await nextTick()
  isLoading.value = true
  userProfile.value = await axiosInstance.get('/api/userProfile/current')
  isLoading.value = false
})

const showSection = (items) => items.some((i) => i.show)

const onSubmit = async (form) => {
  const body = {
    ...userProfile.value,
    ...form
  }

  await axiosInstance.post('/api/userProfile/update', body)
  notify(
    {
      group: 'success',
      title: i18n.t('userProfileInfoUpdated')
    },
    6000
  )
}
</script>

<style lang="scss" scoped></style>
